import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Checkbox, TextField, Typography } from '@mui/material';
import {
  BankReconciliationAccountingLine,
  BankReconciliationLineEdit,
} from 'lib/api/accounting/types';
import React from 'react';

export type BankReconciliationEntryReference = {
  date?: string;
  accountingEntryNumber?: string | number;
  accountingLineId?: string | number;
  movementKey?: string;
  debit?: number;
  credit?: number;
  description?: string;
};

export type TableRowData = {
  id: string;
  rowKey: string;
  line: BankReconciliationAccountingLine;
  edit: BankReconciliationLineEdit;
  date: string;
  businessIdentifier: string;
  description: string;
  debit: number;
  credit: number;
  isReconciled: boolean;
  isReconciledLabel: string;
  observation: string;
  reconciledAt: string;
};

const buildEntryReference = (
  line: BankReconciliationAccountingLine,
): BankReconciliationEntryReference => ({
  date: line.date,
  accountingEntryNumber: line.accountingEntryNumber,
  accountingLineId: line.accountingLineId,
  movementKey:
    typeof line.movementKey === 'string' ? line.movementKey : undefined,
  debit: typeof line.debit === 'number' ? line.debit : undefined,
  credit: typeof line.credit === 'number' ? line.credit : undefined,
  description:
    typeof line.description === 'string' ? line.description : undefined,
});

type EntryDescriptionCellProps = {
  value: string;
  row: TableRowData;
  onOpenEntry: (reference: BankReconciliationEntryReference) => void;
};

export const EntryDescriptionCell: React.FC<EntryDescriptionCellProps> = ({
  value,
  row,
  onOpenEntry,
}) => {
  const handleOpenEntry = () => {
    onOpenEntry(buildEntryReference(row.line));
  };

  return (
    <Typography
      role="button"
      tabIndex={0}
      variant="body2"
      onClick={handleOpenEntry}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleOpenEntry();
        }
      }}
      sx={{
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        '&:hover': {
          color: 'primary.main',
        },
      }}
    >
      {String(value ?? '-')}
    </Typography>
  );
};

type AmountCellProps = {
  value: number;
  formatAmount: (value?: number) => string;
};

export const AmountCell: React.FC<AmountCellProps> = ({
  value,
  formatAmount,
}) => <>{formatAmount(value)}</>;

type ReconciledCheckboxCellProps = {
  row: TableRowData;
  onToggleReconciled: (
    rowKey: string,
    isChecked: boolean,
    reconciledAt: string | null,
  ) => void;
};

export const ReconciledCheckboxCell: React.FC<ReconciledCheckboxCellProps> = ({
  row,
  onToggleReconciled,
}) => (
  <Checkbox
    checked={row.edit.isReconciled}
    onChange={(event) =>
      onToggleReconciled(
        row.rowKey,
        event.target.checked,
        row.edit.reconciledAt ?? null,
      )
    }
  />
);

type ObservationCellProps = {
  row: TableRowData;
  onOpenObservationDialog: (rowKey: string, observation: string) => void;
};

export const ObservationCell: React.FC<ObservationCellProps> = ({
  row,
  onOpenObservationDialog,
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={() => onOpenObservationDialog(row.rowKey, row.edit.observation)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onOpenObservationDialog(row.rowKey, row.edit.observation);
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      cursor: 'pointer',
      borderRadius: 1,
      px: 0.5,
      py: 0.25,
      '&:hover': {
        bgcolor: 'action.hover',
      },
    }}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        display: '-webkit-box',
        overflow: 'hidden',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        textOverflow: 'ellipsis',
      }}
    >
      {row.edit.observation.trim().length > 0 ? row.edit.observation : ''}
    </Typography>
    <EditOutlinedIcon
      fontSize="small"
      color="action"
      sx={{ opacity: 0.7, flexShrink: 0 }}
    />
  </Box>
);

type ReconciledAtCellProps = {
  row: TableRowData;
  emptyEditDate: string;
  onReconciledAtChange: (rowKey: string, reconciledAt: string | null) => void;
};

export const ReconciledAtCell: React.FC<ReconciledAtCellProps> = ({
  row,
  emptyEditDate,
  onReconciledAtChange,
}) => (
  <TextField
    size="small"
    fullWidth
    type="date"
    value={row.edit.reconciledAt ?? emptyEditDate}
    onChange={(event) =>
      onReconciledAtChange(row.rowKey, event.target.value || null)
    }
    InputLabelProps={{ shrink: true }}
  />
);
